import { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useStartWorkflow } from "@/queries/automation-queries";
import { workflowExecutionsUrl } from "@/utils/automations";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Button } from "@/components/flexkit/Button";
import { IconPlay } from "@/assets/icons/geist/IconPlay";

type RunAutomationButtonProps = {
    workflowId: string;
    variant?: "primary" | "secondary";
    children?: React.ReactNode;
    disabled?: boolean;
    "data-tracking-id"?: string;
};

export const RunAutomationButton: FC<RunAutomationButtonProps> = ({
    workflowId,
    variant = "primary",
    children = "Run",
    disabled,
    "data-tracking-id": dataTrackingId,
}) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const startWorkflow = useStartWorkflow();

    const handleRunWorkflow = async () => {
        if (!workflowId) return;

        try {
            const execution = await startWorkflow.mutateAsync(workflowId);
            navigate(workflowExecutionsUrl({ id: execution.id }));
        } catch (error) {
            toast({
                title: "Failed to start automation",
                description: formatError(error),
            });
        }
    };

    return (
        <Button
            variant={variant}
            LeadingIcon={IconPlay}
            onClick={handleRunWorkflow}
            disabled={disabled || startWorkflow.isPending}
            loading={startWorkflow.isPending}
            data-tracking-id={dataTrackingId}
        >
            {children}
        </Button>
    );
};
